import React, { useState, useRef, type ChangeEvent } from 'react';
import styled from 'styled-components';
import { Upload } from '@mui/icons-material';
import { type UploadBoxData } from './types';
import { FILE_MANAGE_ORDER, type FileData } from '../FilesPanel';
import { PAGE_TEXT } from '../../pages/Nomination/components';
import { Alert, type AlertDetails } from '..';
import { DocumentBox } from './DocumentBox';
import { COMMON_FILE_TEXT, FILE_SIZE_LIMITS } from '../FilesPanel/constants';
import { postRequest, type PostRequestData } from '../../utils';

export const UploadBox: React.FunctionComponent<UploadBoxData> = (
  props: UploadBoxData,
) => {
  const { files, handleFilesChange, maximumNum, setLoading } = props;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.target.files === null || e.target.files.length < 1) return;
    const file = e.target.files[0];

    if (file.type !== 'application/pdf') {
      setOpenAlert(true);
      setAlertDetails({
        alertType: 'warning',
        alertContent: 'Only PDF format is accepted.',
      });
      return;
    }

    if (
      file.size < FILE_SIZE_LIMITS.MIN_SIZE ||
      file.size > FILE_SIZE_LIMITS.MAX_SIZE
    ) {
      setOpenAlert(true);
      setAlertDetails({
        alertType: 'warning',
        alertContent: COMMON_FILE_TEXT.SIZE_LIMIT,
      });
      return;
    }

    if (setLoading) setLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const postRequestData: PostRequestData = {
        url: '/files/upload',
        data: formData,
        responseType: 'json',
      };
      const res = await postRequest(postRequestData);

      const id = res.data.urls[0];
      const newFile = { name: file.name, id, link: '' };
      await handleFilesChange(FILE_MANAGE_ORDER.UPLOAD, newFile);
      if (setLoading) setLoading(false);
      setAlertDetails({
        alertContent: 'Uploaded the File Successfully.',
        alertType: 'success',
      });
    } catch (error: any) {
      setAlertDetails({
        alertContent:
          'Uploaded the File Failed.' + error?.response?.data?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const removeFile = async (file: FileData): Promise<void> => {
    // Mod 2024/9/16, not delete from server any more.
    // const res = await axios.delete(`/files/${file.id}`);

    await handleFilesChange(FILE_MANAGE_ORDER.REMOVE, file);
    setAlertDetails({
      alertContent: 'Removed the File Successfully.',
      alertType: 'success',
    });

    setOpenAlert(true);
  };

  const uploadInputkey = files.length; // prevent same file upload causing render problem

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {files.length < maximumNum && (
        <div
          className="flex items-center cursor-pointer mb-2"
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          <input
            key={uploadInputkey}
            className="hidden"
            ref={fileInputRef}
            type="file"
            onChange={uploadFile}
            accept=".pdf"
          />
          <div className="icon-wrapper bg-[#006DAA]">
            <Upload className="text-white" />
          </div>
          <div className="italic text-[#8C8C8C]">
            {PAGE_TEXT.FILE_UPLOAD.REMARK}
          </div>
        </div>
      )}
      <div className="mt-2">
        {files.map((file, index) => {
          return (
            <DocumentBox
              key={index}
              file={file}
              handleRemoveFile={removeFile}
            />
          );
        })}
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 95%;
  width: 100%;
  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    height: 28px;
    width: 28px;
    margin-right: 16px;
    border-radius: 4px;
  }
`;
